// src/pages/Home.page.js
import React, { Suspense, useEffect, useLayoutEffect, useContext, useState, useCallback, useRef, useMemo } from "react";
import { scrollToPageTop } from "../utils/scrollToPageTop";
import { I18nextProvider, useTranslation } from "react-i18next";
import { useParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import i18n from "../utils/language/i18n";
import { apiClient } from "../utils/api/api";
import tokenManager from "../utils/security/tokenManager";
import localforage from "localforage";
import { stripPersonnummerStorageFields } from "../utils/personnummerClient";
import { useNavigationSync } from "../utils/hooks/useNavigationSync";

import NavBar from "../utils/components/navbar/NavBar";
import DataProvider from "../contexts/DataContext";
import {
  LandingPage,
  MessagePage,
  ProfilePage,
  SettingsPage,
  ManageFamily,
  AddChildPage,
  MembershipPage,
  DigitalCardPage,
  BoardPage,
  DocumentationPage,
  MembersGroupAddUsersPage,
  BeltsPage,
  PendingPromotionPage,
  ColorPromotionPage,
  PendingColorPromotionPage,
  MentorListPage,
  MentorManagerPage,
  BeltPromotionPage,
  BeltAdminPage,
  PromotionByGroupPage,
  ClubSettingsPage,
  UserAdminPage,
  AttendancePage,
  AchievementsPage,
  MyPaymentsPage,
  MyMentorPage,
  MySmiliesPage,
  ClubAttendanceStats,
  QRScannerPage,
  SchedulePage,
  ScheduleHubPage,
  InstructorSchedulePage,
  InstructorAvailabilityPage,
  QRResultPage,
  TimerPage,
  NewContentPage,
  TrainingProgramContentPage,
  VideoLibraryContentPage,
  NewsPage,
  NewsDetailPage,
  TrainingProgramPage,
  TrainingProgramDetailPage,
  VideoLibraryPage,
  TimeRegistrationPage,
  ApprovalCenterPage,
  FinancePage,
  ExpensesPage,
  TrainingGroupsPage,
  GroupMailingsPage,
  LokStodPage,
  AnmalningarPage,
  PosAdminPage,
  PosShopPage,
  PosShopCheckoutPage,
  PosShopKioskPage,
  BirthdayDisplayPage,
} from "./homeLazyPages";
import { SupabaseUserContext } from "../contexts/supabaseUser.context";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";

import { useMediaQuery, Box, CircularProgress, CssBaseline, Alert } from '@mui/material';
import { ThemeProvider } from "../contexts/ThemeContext";
import { useTheme } from "../contexts/ThemeContext";
import { FinanceBillingProvider } from "../comp/admin/finance/FinanceBillingContext";
import { ClubScopeProvider, useClubScope } from "../contexts/ClubScopeContext";
import { resolveClubId } from "../utils/clubScope";
import { FINANCE_TAB } from "../comp/admin/finance/financePageTabs";
import PlatformSuspendedScreen from "../comp/PlatformSuspendedScreen";
import MembershipPaymentRequiredScreen from "../comp/MembershipPaymentRequiredScreen";
import CompleteProfileScreen from "../comp/CompleteProfileScreen";
import CompletePersonnummerScreen from "../comp/CompletePersonnummerScreen";
import { PERSONNUMMER_UPDATE_WALL_ENABLED } from "../utils/personnummerUpdateWall";
import { userHasAttendanceKioskLogin, userHasBirthdayKioskLogin } from "../realm/settingsPermissions";
import { resolveSessionPolicy } from "../utils/security/sessionPolicy";
import { isCashPaymentPending, CASH_PENDING_ALLOWED_PAGES } from "../utils/cashPaymentPending";
import {
  DRAWER_WIDTH,
  mainContentShellSx,
  kioskMainShellSx,
} from "../constants/layout";
import PageBreadcrumbs from "../utils/components/PageBreadcrumbs";
import PageHelpCornerHost from "../utils/components/pageInfo/PageHelpCornerHost";
import BugReportHost from "../components/BugReportHost";
import SupportErrorOfferHost from "../components/SupportErrorOfferHost";
import { PageHelpOverrideProvider } from "../utils/components/pageInfo/PageHelpOverrideContext";
import {
  PAGE_ROOT,
  resolveBreadcrumbTrail,
} from "../navigation/pageBreadcrumbs";

const ThemeWrapper = ({ children }) => {
  const { theme } = useTheme();
  return (
    <MuiThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          transition: "background-color 0.3s ease",
        }}
      >
        {children}
      </Box>
    </MuiThemeProvider>
  );
};

function useMemberAllowedPages(userInfo, landingRestrict) {
  const { apiMe } = useClubScope();
  return useMemo(() => {
    if (landingRestrict) return ["LandingPage"];
    if (isCashPaymentPending(apiMe, userInfo)) return CASH_PENDING_ALLOWED_PAGES;
    return null;
  }, [landingRestrict, apiMe, userInfo]);
}

function HomeAllowedPagesGuard({ selectedPage, navigateToPage, userInfo, landingRestrict }) {
  const allowedPages = useMemberAllowedPages(userInfo, landingRestrict);
  useEffect(() => {
    if (!allowedPages || allowedPages.includes(selectedPage)) return;
    navigateToPage("LandingPage");
  }, [allowedPages, selectedPage, navigateToPage]);
  return null;
}

function ScopedHomeNavBar({ userInfo, landingRestrict, ...rest }) {
  const allowedPages = useMemberAllowedPages(userInfo, landingRestrict);
  return (
    <NavBar
      {...rest}
      userInfo={userInfo}
      allowedMenuPages={allowedPages}
    />
  );
}

function CashPendingAccessBanner({ userInfo, landingRestrict }) {
  const { t } = useTranslation();
  const { apiMe } = useClubScope();
  if (landingRestrict || !isCashPaymentPending(apiMe, userInfo)) return null;
  return (
    <Alert severity="info" sx={{ mb: 2, borderRadius: "8px" }}>
      {t("cash_pending_app_banner")}
    </Alert>
  );
}

function HomeMembershipGate({ userInfo, setUserInfoRefreshKey, children }) {
  const { apiMe, refreshApiMe } = useClubScope();
  const [paymentOverride, setPaymentOverride] = useState(null);
  const [trialOverride, setTrialOverride] = useState(null);
  const row = userInfo?.[0];
  const isMemberOnly = row && !row.isAdmin && !row.isCoach;

  const profileRequired = apiMe?.profileCompletion?.required === true;
  const personnummerRequired =
    PERSONNUMMER_UPDATE_WALL_ENABLED &&
    apiMe?.personnummerCompletion?.required === true;
  const cashPending = isCashPaymentPending(apiMe, userInfo);
  const paymentRequired =
    !cashPending &&
    (Boolean(paymentOverride) || apiMe?.membershipAccess === "payment_required");
  const paymentUrl =
    paymentOverride ??
    (apiMe?.membershipAccess === "payment_required" ? apiMe.paymentRedirectUrl || null : null);
  const trialEndsAt = trialOverride ?? apiMe?.trialEndsAt ?? null;

  if (profileRequired) {
    return (
      <ThemeProvider>
        <ThemeWrapper>
          <CssBaseline />
          <CompleteProfileScreen
            userInfo={userInfo}
            profileCompletion={apiMe?.profileCompletion}
            onCompleted={async (result) => {
              if (
                result?.membershipAccess === "payment_required" &&
                result?.paymentRedirectUrl
              ) {
                setPaymentOverride(result.paymentRedirectUrl);
                setTrialOverride(result.trialEndsAt || null);
              }
              await refreshApiMe();
              setUserInfoRefreshKey((k) => k + 1);
            }}
          />
        </ThemeWrapper>
      </ThemeProvider>
    );
  }

  if (personnummerRequired) {
    return (
      <ThemeProvider>
        <ThemeWrapper>
          <CssBaseline />
          <CompletePersonnummerScreen
            userInfo={userInfo}
            onCompleted={async () => {
              await refreshApiMe();
              setUserInfoRefreshKey((k) => k + 1);
            }}
          />
        </ThemeWrapper>
      </ThemeProvider>
    );
  }

  if (!isMemberOnly) {
    return children;
  }

  if (paymentRequired) {
    return (
      <ThemeProvider>
        <ThemeWrapper>
          <CssBaseline />
          <Box
            sx={{
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MembershipPaymentRequiredScreen
              paymentUrl={paymentUrl}
              trialEndsAt={trialEndsAt}
              redirecting={Boolean(paymentUrl)}
            />
          </Box>
        </ThemeWrapper>
      </ThemeProvider>
    );
  }

  return children;
}

export default function Home(props) {
  const { user, setSessionPolicy } = useContext(SupabaseUserContext);
  const { studentID } = useParams(); // Get studentID from URL params
  const location = useLocation(); // Get location for query params
  const navigate = useNavigate(); // For URL navigation
  const [searchParams, setSearchParams] = useSearchParams(); // For URL params
  const [userInfo, setUserInfo] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userInfoRefreshKey, setUserInfoRefreshKey] = useState(0);
  const [platformSuspended, setPlatformSuspended] = useState(false);
  const [platformSuspendedClubID, setPlatformSuspendedClubID] = useState(null);
  const [landingAccessBlock, setLandingAccessBlock] = useState(null);
  const restrictNavToHome = [
    "session_expired",
    "under_review",
    "account_not_found",
    "connection_error",
    "not_logged_in",
  ].includes(landingAccessBlock?.type);
  const hasLoadedFromIndexedDB = useRef(false);
  const lastLoadedEmail = useRef(null);
  const [selectedPage, setSelectedPage] = useState(() => {
    try {
      if (window.location.pathname !== "/" && window.location.pathname !== "") {
        return "LandingPage";
      }
      const urlParams = new URLSearchParams(window.location.search);
      const p = urlParams.get("page");
      if (!p || p.trim() === "") return "LandingPage";
      return p;
    } catch {
      return "LandingPage";
    }
  });
  const [selectedId, setSelectedId]     = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [financeTargetTab, setFinanceTargetTab] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState({});
  const [colorPromotionUser, setColorPromotionUser] = useState({});
  const isMobile = useMediaQuery("(max-width:600px)");
  const [scannedValue, setScannedValue] = useState('');
  const [qrResultRefreshTrigger, setQrResultRefreshTrigger] = useState(0);
  /** One-time redirect to QR scanner after login for users with attendance_kiosk permission only */
  const attendanceScannerRedirectDoneRef = useRef(false);
  /** One-time redirect to birthday display after login for users with birthday_kiosk permission */
  const birthdayDisplayRedirectDoneRef = useRef(false);

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const drawerWidth = DRAWER_WIDTH;
  const [breadcrumbTrail, setBreadcrumbTrail] = useState([PAGE_ROOT]);
  const [breadcrumbSuffix, setBreadcrumbSuffix] = useState(null);
  const [newsDetailTitle, setNewsDetailTitle] = useState(null);
  const [trainingProgramDetailTitle, setTrainingProgramDetailTitle] = useState(null);
  const skipBreadcrumbSyncRef = useRef(false);

  const navigateToPage = useCallback((page) => {
    skipBreadcrumbSyncRef.current = true;
    setBreadcrumbTrail((prev) => resolveBreadcrumbTrail(prev, page));
    setSelectedPage(page);
    if (isMobile || userHasAttendanceKioskLogin(userInfo) || userHasBirthdayKioskLogin(userInfo)) {
      setDrawerOpen(false);
    }
  }, [isMobile, userInfo]);

  useEffect(() => {
    if (skipBreadcrumbSyncRef.current) {
      skipBreadcrumbSyncRef.current = false;
      return;
    }
    setBreadcrumbTrail((prev) => {
      if (selectedPage === PAGE_ROOT) return [PAGE_ROOT];
      const last = prev[prev.length - 1];
      if (last === selectedPage) return prev;
      return resolveBreadcrumbTrail(prev, selectedPage);
    });
  }, [selectedPage]);

  useEffect(() => {
    if (selectedPage !== "FinancePage" && selectedPage !== "PosAdminPage") {
      setBreadcrumbSuffix(null);
    }
    if (selectedPage !== "NewsDetail") {
      setNewsDetailTitle(null);
    }
    if (selectedPage !== "TrainingProgramDetail") {
      setTrainingProgramDetailTitle(null);
    }
  }, [selectedPage]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__NEWERA_SELECTED_PAGE__ = selectedPage || "";
    }
    return () => {
      if (typeof window !== "undefined") {
        window.__NEWERA_SELECTED_PAGE__ = "";
      }
    };
  }, [selectedPage]);

  // Function to trigger QRResultPage refresh
  const triggerQRResultRefresh = () => {
    setQrResultRefreshTrigger(prev => prev + 1);
  };

  // Use custom hook for URL synchronization
  useNavigationSync(selectedPage, setSelectedPage);

  /** Legacy deep links (financeTargetTab === 1) → dedicated training groups page. */
  useEffect(() => {
    if (financeTargetTab === FINANCE_TAB.GROUP_LIST) {
      navigateToPage("TrainingGroupsPage");
      setFinanceTargetTab(null);
    }
  }, [financeTargetTab, setFinanceTargetTab, navigateToPage]);

  // Handle QR code redirects
  useEffect(() => {
    // Check if we have a studentID from URL params (QR code redirect)
    if (studentID) {
      setScannedValue(studentID);
      navigateToPage('QRResultPage');
      return;
    }

    // Check for simple redirect (device provides its own student ID)
    if (location.pathname === '/qr/redirect') {
      // Check for useDeviceStudentID parameter
      const urlParams = new URLSearchParams(location.search);
      const useDeviceStudentID = urlParams.get('useDeviceStudentID');
      
      if (useDeviceStudentID === 'true') {
        // Use current user's student ID from device
        const currentStudentID = userInfo[0]?.studentID;
        if (currentStudentID) {
          setScannedValue(currentStudentID);  // ← This is the key fix!
          navigateToPage('QRResultPage');
        }
      }
      return;
    }

    // Check for query parameters (alternative QR code format)
    const urlParams = new URLSearchParams(location.search);
    const pageParam = urlParams.get('page');
    const studentIDParam = urlParams.get('studentID');
    
    if (pageParam === 'QRResultPage' && studentIDParam) {
      setScannedValue(studentIDParam);
      navigateToPage('QRResultPage');
      return;
    }
  }, [studentID, location.pathname, location.search, userInfo, navigateToPage]);

  const getUsersByEmail = useCallback(async (email, accessToken) => {
    
    if (!accessToken) {
      throw new Error("No access token provided");
    }
    
    try {
      const response = await apiClient.get(
        `/api/user?email=${encodeURIComponent(email)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      
      setIsLoading(true);

      if (!user?.email) {
        setIsLoading(false);
        return;
      }
      const accessToken = tokenManager.getToken();
      if (!accessToken) {
        console.error("No realmJwt in localStorage—cannot fetch");
        setIsLoading(false);
        return;
      }

      try {
        // Always fetch from API to get the allowed set (backend enforces own email only)
        const data = await getUsersByEmail(user.email, accessToken);
        
        // Handle both single user object and array responses
        if (data) {
          const allowedUsers = Array.isArray(data) ? data : [data];
          
          // SECURITY: Validate stored activeUser against allowed set - never trust IndexedDB blindly
          let storedActive = null;
          try {
            storedActive = await localforage.getItem("activeUser");
          } catch (err) {
            console.warn("IndexedDB read failed:", err);
          }

          let userData = null;
          if (storedActive && allowedUsers.length > 0) {
            const foundInAllowed = allowedUsers.find((u) => u.studentID === storedActive.studentID);
            // Only use stored if it exists in the API response (same email / family)
            if (foundInAllowed) {
              userData = stripPersonnummerStorageFields({ ...storedActive, ...foundInAllowed });
              hasLoadedFromIndexedDB.current = true;
            } else {
              // Stored user is not in allowed set - clear invalid cache
              try {
                await localforage.removeItem("activeUser");
              } catch (e) {}
            }
          }

          if (!userData && allowedUsers.length > 0) {
            userData = stripPersonnummerStorageFields(
              allowedUsers.find((u) => u.isParent) || allowedUsers[0]
            );
            try {
              await localforage.setItem("activeUser", userData);
            } catch (err) {
              console.error("IndexedDB write failed:", err);
            }
          }
          
          if (userData) {
            setUserInfo([userData]);
            lastLoadedEmail.current = userData.email;

            // Persist small flags in localStorage
            localStorage.setItem("club", userData.club || "");
            // Use authenticated user's email (Supabase) - never the child's email when parent views as kid
            // Fixes bug: parent switching to kid with other parent's email would overwrite localStorage
            const emailToStore = user?.email || user?._profile?.data?.email || userData?.email || "";
            if (emailToStore) localStorage.setItem("email", emailToStore);
          } else {
            setUserInfo([]);
          }
        } else {
          setUserInfo([]);
        }
      } catch (err) {
        console.error("getUsersByEmail failed", err.response?.data || err.message);
        const status = err.response?.status;
        const errCode = err.response?.data?.error;
        if (errCode === "club_platform_suspended") {
          setPlatformSuspended(true);
          setPlatformSuspendedClubID(err.response?.data?.clubID ?? null);
          setUserInfo([]);
          return;
        }
        // If user not found (404) or forbidden (403), clear userInfo and cache
        const is403 = status === 403;
        const is401 = status === 401;
        const is404 = status === 404 || err.message?.includes('not found') || err.response?.data?.error === 'User not found';
        if (is401 || is404 || is403) {
          setUserInfo([]);
          try {
            await localforage.removeItem("userInfo");
            await localforage.removeItem("activeUser");
          } catch (forageErr) {
            console.warn("Failed to clear IndexedDB:", forageErr);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, [user?._profile?.data?.email, user?.email, getUsersByEmail, userInfoRefreshKey]);

  useEffect(() => {
    setSessionPolicy(resolveSessionPolicy(userInfo));
  }, [userInfo, setSessionPolicy]);

  useEffect(() => {
    const onPermissionsUpdated = () => setUserInfoRefreshKey((k) => k + 1);
    window.addEventListener("settingsPermissionsUpdated", onPermissionsUpdated);
    return () => window.removeEventListener("settingsPermissionsUpdated", onPermissionsUpdated);
  }, []);

  // Reset kiosk redirect flag when logged-out / no active profile
  useEffect(() => {
    if (!userInfo?.[0]) {
      attendanceScannerRedirectDoneRef.current = false;
      birthdayDisplayRedirectDoneRef.current = false;
    }
  }, [userInfo]);

  // Attendance kiosk permission: open QR scanner once after user profile loads (does not alter other login flows)
  useEffect(() => {
    if (isLoading) return;
    if (!userHasAttendanceKioskLogin(userInfo)) return;
    if (userHasBirthdayKioskLogin(userInfo)) return;
    if (attendanceScannerRedirectDoneRef.current) return;

    // Let existing QR deep-link logic handle these routes
    if (studentID) return;
    if (location.pathname?.startsWith("/qr")) return;
    const sp = new URLSearchParams(location.search || "");
    const pageParam = sp.get("page");
    if (pageParam && pageParam !== "LandingPage") return;

    attendanceScannerRedirectDoneRef.current = true;
    navigateToPage("QRScannerPage");
  }, [
    isLoading,
    userInfo,
    studentID,
    location.pathname,
    location.search,
    navigateToPage,
  ]);

  // Birthday kiosk: open fullscreen display after profile loads (runs in layout effect so it wins over URL sync)
  useLayoutEffect(() => {
    if (isLoading) return;
    if (!userHasBirthdayKioskLogin(userInfo)) return;
    if (studentID) return;
    if (location.pathname?.startsWith("/qr")) return;
    if (selectedPage === "BirthdayDisplayPage") return;

    birthdayDisplayRedirectDoneRef.current = true;
    navigateToPage("BirthdayDisplayPage");
  }, [
    isLoading,
    userInfo,
    selectedPage,
    studentID,
    location.pathname,
    location.search,
    navigateToPage,
  ]);

  const isAttendanceKioskUser = userHasAttendanceKioskLogin(userInfo);
  const isBirthdayKioskUser = userHasBirthdayKioskLogin(userInfo);

  useEffect(() => {
    if (isAttendanceKioskUser || isBirthdayKioskUser) {
      setDrawerOpen(false);
    }
  }, [isAttendanceKioskUser, isBirthdayKioskUser]);

  useEffect(() => {
    if (isAttendanceKioskUser || isBirthdayKioskUser) return;
    setDrawerOpen(!isMobile);
  }, [isMobile, isAttendanceKioskUser, isBirthdayKioskUser]);

  const isPosKioskMode = selectedPage === "PosShopKioskPage";
  const isBirthdayKioskMode = selectedPage === "BirthdayDisplayPage";
  const [timerImmersive, setTimerImmersive] = useState(false);
  const isTimerKioskMode = selectedPage === "TimerPage" && timerImmersive;
  const hideAppChrome = isPosKioskMode || isTimerKioskMode || isBirthdayKioskMode;

  useEffect(() => {
    if (selectedPage !== "TimerPage") setTimerImmersive(false);
  }, [selectedPage]);

  useLayoutEffect(() => {
    scrollToPageTop();
  }, [selectedPage]);

  if (platformSuspended) {
    return (
      <ThemeProvider>
        <ThemeWrapper>
          <CssBaseline />
          <PlatformSuspendedScreen clubID={platformSuspendedClubID} />
        </ThemeWrapper>
      </ThemeProvider>
    );
  }

  return (
    <ClubScopeProvider userInfo={userInfo}>
      <HomeMembershipGate userInfo={userInfo} setUserInfoRefreshKey={setUserInfoRefreshKey}>
      <I18nextProvider i18n={i18n}>
        <div
          style={{
            backgroundImage: `url("bjjnation_background.jpg")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "fixed",
            width: "100%",
            height: "100%",
            zIndex: -1,
            opacity: isTimerKioskMode || isBirthdayKioskMode ? 0 : 0.3,
          }}
        />
        <ThemeProvider>
          <ThemeWrapper>
            <CssBaseline />
            <PageHelpOverrideProvider>
            <DataProvider userInfo={userInfo}>
            {!hideAppChrome && (
            <ScopedHomeNavBar
              drawerOpen={drawerOpen}
              setDrawerOpen={setDrawerOpen}
              selectedPage={selectedPage}
              setSelectedPage={navigateToPage}
              userInfo={userInfo}
              setUserInfo={setUserInfo}
              isLoading={isLoading}
              contentDrawerWidth={drawerWidth}
              landingRestrict={restrictNavToHome}
            />
            )}
            <HomeAllowedPagesGuard
              selectedPage={selectedPage}
              navigateToPage={navigateToPage}
              userInfo={userInfo}
              landingRestrict={restrictNavToHome}
            />
            <FinanceBillingProvider
              clubID={resolveClubId(userInfo)}
              isActive={
                selectedPage === "FinancePage" ||
                selectedPage === "ExpensesPage" ||
                selectedPage === "TrainingGroupsPage" ||
                selectedPage === "LokStodPage" ||
                selectedPage === "ClubSettingsPage" ||
                selectedPage === "PosAdminPage"
              }
            >
            <Box
              component="main"
              sx={hideAppChrome ? kioskMainShellSx : mainContentShellSx(drawerOpen, isMobile)}
            >
              <PageHelpCornerHost selectedPage={selectedPage} hidden={hideAppChrome} />
              {!hideAppChrome ? (
                <CashPendingAccessBanner userInfo={userInfo} landingRestrict={restrictNavToHome} />
              ) : null}
              {!hideAppChrome && user && tokenManager.getToken() ? (
                <BugReportHost selectedPage={selectedPage} userInfo={userInfo} />
              ) : null}
              {!hideAppChrome && user && tokenManager.getToken() ? (
                <SupportErrorOfferHost selectedPage={selectedPage} userInfo={userInfo} />
              ) : null}
              {selectedPage !== "TimerPage" &&
                selectedPage !== "QRScannerPage" &&
                selectedPage !== "QRResultPage" &&
                selectedPage !== "DigitalCardPage" &&
                selectedPage !== "BirthdayDisplayPage" && (
              <PageBreadcrumbs
                selectedPage={selectedPage}
                trail={breadcrumbTrail}
                onNavigate={navigateToPage}
                dynamicLabels={{
                  ...(newsDetailTitle ? { NewsDetail: newsDetailTitle } : {}),
                  ...(trainingProgramDetailTitle ? { TrainingProgramDetail: trainingProgramDetailTitle } : {}),
                }}
                suffixLabel={breadcrumbSuffix}
                compact={isPosKioskMode}
              />
              )}
              <MessagePage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <LandingPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} setIsLoading={setIsLoading} onAccessBlockChange={setLandingAccessBlock} />
              <SettingsPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} setFinanceTargetTab={setFinanceTargetTab} />
              <ProfilePage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} allowEdit={userInfo[0]?.allowEdit || false} isLoading={isLoading} setIsLoading={setIsLoading} />
              <Suspense
                fallback={
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                  </Box>
                }
              >
              <AchievementsPage selectedPage={selectedPage} userInfo={userInfo} setIsLoading={setIsLoading} isLoading={isLoading} />
              <MyPaymentsPage selectedPage={selectedPage} userInfo={userInfo} />
              <MyMentorPage selectedPage={selectedPage} userInfo={userInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <MySmiliesPage selectedPage={selectedPage} userInfo={userInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <ManageFamily selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} setColorPromotionUser={setColorPromotionUser} colorPromotionUser={colorPromotionUser} isLoading={isLoading} setIsLoading={setIsLoading} />
              <AddChildPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} />
              <DigitalCardPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <BoardPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} />
              <DocumentationPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} />
              <MembershipPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <MembersGroupAddUsersPage selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} setFinanceTargetTab={setFinanceTargetTab} />
              <BeltsPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <PendingPromotionPage selectedPage={selectedPage} setSelectedPage={navigateToPage} isLoading={isLoading} setIsLoading={setIsLoading} userInfo={userInfo} setUserInfo={setUserInfo} />
              <ColorPromotionPage selectedPage={selectedPage} setSelectedPage={navigateToPage} isLoading={isLoading} setIsLoading={setIsLoading} userInfo={userInfo} setUserInfo={setUserInfo} setColorPromotionUser={setColorPromotionUser} colorPromotionUser={colorPromotionUser} />
              <PendingColorPromotionPage selectedPage={selectedPage} setSelectedPage={navigateToPage} isLoading={isLoading} setIsLoading={setIsLoading} userInfo={userInfo} setUserInfo={setUserInfo} setColorPromotionUser={setColorPromotionUser} colorPromotionUser={colorPromotionUser} />
              <MentorListPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
                              <MentorManagerPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
                          
                             <BeltPromotionPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} setDrawerOpen={setDrawerOpen} />
              <BeltAdminPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <ClubSettingsPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} setFinanceTargetTab={setFinanceTargetTab} />
              <UserAdminPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <PromotionByGroupPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} />
              <AttendancePage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <ClubAttendanceStats selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <QRScannerPage setSelectedPage={navigateToPage} currentUser={userInfo[0]} selectedPage={selectedPage} setScannedValue={setScannedValue} triggerQRResultRefresh={triggerQRResultRefresh} setDrawerOpen={setDrawerOpen} />
              <ScheduleHubPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} isLoading={isLoading} setIsLoading={setIsLoading} />
              <SchedulePage selectedPage={selectedPage} setSelectedPage={navigateToPage} currentUser={userInfo[0]} isLoading={isLoading} setIsLoading={setIsLoading} />
              <InstructorSchedulePage selectedPage={selectedPage} setSelectedPage={navigateToPage} currentUser={userInfo[0]} isLoading={isLoading} setIsLoading={setIsLoading} />
              <InstructorAvailabilityPage selectedPage={selectedPage} setSelectedPage={navigateToPage} currentUser={userInfo[0]} isLoading={isLoading} setIsLoading={setIsLoading} />
              <QRResultPage selectedPage={selectedPage} setScannedValue={setScannedValue} scannedValue={scannedValue} setSelectedPage={navigateToPage} currentUser={userInfo[0]} isLoading={isLoading} setIsLoading={setIsLoading} qrResultRefreshTrigger={qrResultRefreshTrigger} />
              <TimerPage
                selectedPage={selectedPage}
                setSelectedPage={navigateToPage}
                userInfo={userInfo}
                setUserInfo={setUserInfo}
                onImmersiveChange={setTimerImmersive}
              />
              <NewContentPage 
                selectedPage={selectedPage} 
                setSelectedPage={navigateToPage} 
                currentUser={userInfo[0]}
                onChangePage={(page, id) => {
                  setPreviousPage(selectedPage);
                  setNewsDetailTitle(null);
                  setSelectedId(id);
                  navigateToPage(page);
                }}
              />
              <TrainingProgramContentPage
                selectedPage={selectedPage}
                setSelectedPage={navigateToPage}
                currentUser={userInfo[0]}
                onChangePage={(page, id) => {
                  setPreviousPage(selectedPage);
                  setTrainingProgramDetailTitle(null);
                  setSelectedId(id);
                  navigateToPage(page);
                }}
              />
              <VideoLibraryContentPage
                selectedPage={selectedPage}
                userInfo={userInfo}
              />
              <ApprovalCenterPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setUserInfo={setUserInfo} />
              <FinancePage
                selectedPage={selectedPage}
                setSelectedPage={navigateToPage}
                setSelectedGroup={setSelectedGroup}
                userInfo={userInfo}
                financeTargetTab={financeTargetTab}
                setFinanceTargetTab={setFinanceTargetTab}
                onBreadcrumbSuffix={setBreadcrumbSuffix}
              />
              <ExpensesPage selectedPage={selectedPage} userInfo={userInfo} />
              <TrainingGroupsPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} setHomeSelectedGroup={setSelectedGroup} />
              <GroupMailingsPage selectedPage={selectedPage} userInfo={userInfo} />
              <LokStodPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} />
              <AnmalningarPage selectedPage={selectedPage} userInfo={userInfo} />
              <PosAdminPage selectedPage={selectedPage} setSelectedPage={navigateToPage} onBreadcrumbSuffix={setBreadcrumbSuffix} />
              <PosShopPage selectedPage={selectedPage} setSelectedPage={navigateToPage} />
              <PosShopCheckoutPage selectedPage={selectedPage} setSelectedPage={navigateToPage} userInfo={userInfo} />
              <PosShopKioskPage
                selectedPage={selectedPage}
                setSelectedPage={navigateToPage}
                userInfo={userInfo}
                setDrawerOpen={setDrawerOpen}
              />
              <BirthdayDisplayPage
                selectedPage={selectedPage}
                userInfo={userInfo}
                setDrawerOpen={setDrawerOpen}
              />
              <NewsPage
                selectedPage={selectedPage}
                onChangePage={(page, id) => {
                  setPreviousPage(selectedPage);
                  setNewsDetailTitle(null);
                  setSelectedId(id);
                  navigateToPage(page);
                }}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />

              <NewsDetailPage
                selectedPage={selectedPage}
                contentId={selectedId}
                previousPage={previousPage}
                onChangePage={navigateToPage}
                onTitleLoaded={setNewsDetailTitle}
              />
              <TrainingProgramPage
                selectedPage={selectedPage}
                onChangePage={(page, id) => {
                  setPreviousPage(selectedPage);
                  setTrainingProgramDetailTitle(null);
                  setSelectedId(id);
                  navigateToPage(page);
                }}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
              <TrainingProgramDetailPage
                selectedPage={selectedPage}
                contentId={selectedId}
                onTitleLoaded={setTrainingProgramDetailTitle}
              />
              <VideoLibraryPage
                selectedPage={selectedPage}
                userInfo={userInfo}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
              <TimeRegistrationPage
                selectedPage={selectedPage}
                setSelectedPage={navigateToPage}
                userInfo={userInfo}
              />
              </Suspense>
              {/* <FooterPage /> */}
            </Box>
            </FinanceBillingProvider>
          </DataProvider>
          </PageHelpOverrideProvider>
          </ThemeWrapper>
        </ThemeProvider>
      </I18nextProvider>
      </HomeMembershipGate>
    </ClubScopeProvider>
  );
}
